import {AgentSelector} from '../AgentSelector'
import type {SelectedAgent} from '@github-ui/agent-sessions-types/agent-identity'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'
import {ConsecutiveCAPI401sProvider} from '@github-ui/copilot-shared-ui/capi/ConsecutiveCAPI401sContext'
import {newRepoPath} from '@github-ui/paths'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {ImageIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import type {RefObject} from 'react'

import {ImageAttachButton} from './ImageAttachButton'
import imageAttachStyles from './ImageAttachButton.module.css'
import styles from './TaskChatInputToolbar.module.css'
import {ToolbarButtonSkeleton} from './ToolbarButtonSkeleton'

/**
 * Builds the "New repository" link URL, pre-filling the owner and prompt when available.
 */
export function buildNewRepositoryUrl(currentUserLogin?: string, currentPrompt?: string): string {
  const params = new URLSearchParams()
  if (currentUserLogin) {
    params.set('owner', currentUserLogin)
  }
  if (currentPrompt?.trim()) {
    params.set('prompt', currentPrompt.trim())
  }
  const query = params.toString()
  return query ? `${newRepoPath()}?${query}` : newRepoPath()
}

type TaskChatInputToolbarProps = {
  selectedRepo: AgentDropdownRepository | undefined
  selectedBranch: string | undefined
  selectedAgent: SelectedAgent
  onAgentSelect: (agent: SelectedAgent) => void
  agentSelectionEnabled?: boolean
  /** Restricts the agent selector to repository custom agents, hiding top-level agents. */
  customAgentsOnly?: boolean
  /** Ref to the textarea for image attachment insertion. When provided, renders the ImageAttachButton. */
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  /** Whether the image limit has been reached. */
  isAtImageLimit?: boolean
  /** Hides the cloud image attach button; AHP environments attach via `onAttachEnvironmentFiles` instead. */
  hideImageAttach?: boolean
  /** Opens the AHP file picker. Rendered in place of the cloud image attach button. */
  onAttachEnvironmentFiles?: () => void
  isUploadingEnvironmentFiles?: boolean
  /** Disables every control in the toolbar (e.g. while a task is being submitted). */
  disabled?: boolean
}

export const TaskChatInputToolbar = ({
  selectedRepo,
  selectedBranch,
  selectedAgent,
  onAgentSelect,
  agentSelectionEnabled = true,
  customAgentsOnly = false,
  textAreaRef,
  isAtImageLimit,
  hideImageAttach = false,
  onAttachEnvironmentFiles,
  isUploadingEnvironmentFiles = false,
  disabled = false,
}: TaskChatInputToolbarProps) => {
  return (
    <ConsecutiveCAPI401sProvider>
      <fieldset disabled={disabled} className={styles.container}>
        {agentSelectionEnabled &&
          (!selectedRepo ? (
            <ToolbarButtonSkeleton width="32px" />
          ) : (
            <AgentSelector
              repo={selectedRepo}
              branch={selectedBranch || selectedRepo.defaultBranch}
              selectedAgent={selectedAgent}
              onSelectAgent={agent => onAgentSelect(agent)}
              customAgentsOnly={customAgentsOnly}
              iconOnly
              triggerClassName={styles.invisibleSelectorButton}
            />
          ))}

        {!hideImageAttach && (
          <ImageAttachButton
            textAreaRef={textAreaRef}
            repositoryId={selectedRepo?.id?.toString()}
            disabled={!selectedRepo}
            isAtImageLimit={isAtImageLimit}
          />
        )}

        {onAttachEnvironmentFiles && (
          <IconButton
            icon={ImageIcon}
            aria-label="Attach files"
            variant="invisible"
            className={imageAttachStyles.attachButton}
            inactive={isUploadingEnvironmentFiles}
            aria-disabled={isUploadingEnvironmentFiles}
            onClick={isUploadingEnvironmentFiles ? undefined : onAttachEnvironmentFiles}
          />
        )}
      </fieldset>
    </ConsecutiveCAPI401sProvider>
  )
}

const COPILOT_REPOSITORIES_API_ENDPOINT = '/copilot/agent-sessions/repositories'

interface AgentRepositoryResponse {
  id: number
  graphQLId: string
  nameWithOwner: string
  enabled: boolean
  name: string
  owner: string
  defaultBranch: string
  visibility: string
  isEmpty: boolean
}

/**
 * Shared function to fetch Copilot-enabled repositories from the API
 */
export async function queryForRepos(
  query: string = '',
  defaultOwner?: string,
  repoToIncludeInTopRepos?: string,
): Promise<AgentDropdownRepository[]> {
  const url = new URL(COPILOT_REPOSITORIES_API_ENDPOINT, window.location.origin)

  if (query.trim()) {
    url.searchParams.set('q', query.trim())
  }

  if (defaultOwner) {
    url.searchParams.set('defaultOwner', defaultOwner)
  }

  if (repoToIncludeInTopRepos) {
    url.searchParams.set('includeInTopRepos', repoToIncludeInTopRepos)
  }

  const response = await verifiedFetchJSON(url.href)

  if (response.ok) {
    const {repositories} = (await response.json()) as {repositories: AgentRepositoryResponse[]}
    return repositories.map((repo: AgentRepositoryResponse) => {
      return {
        id: repo.id,
        graphQLId: repo.graphQLId,
        nameWithOwner: repo.nameWithOwner,
        enabled: repo.enabled,
        name: repo.nameWithOwner,
        nameOnly: repo.name,
        owner: repo.owner,
        defaultBranch: repo.defaultBranch,
        private: repo.visibility !== 'public',
        isEmpty: repo.isEmpty,
      }
    })
  }
  return []
}
